import React from 'react'
import axios from 'axios'
import {v4} from 'uuid'
import {generatePath} from 'react-router-dom'
import {StoreContext} from '../store'

const METHOD = {
  GET: 'GET',
  POST: 'POST',
  DELETE: 'DELETE',
}

export const CONTROLLER = {
  AUTH: {
    method: METHOD.GET,
    url: '/auth',
  },
  LOGOUT: {
    method: METHOD.POST,
    url: '/logout',
  },
  LOGIN: {
    method: METHOD.POST,
    url: '/login',
  },
  SIGNUP: {
    method: METHOD.POST,
    url: '/register',
  },
  FORGOT_PASSWORD: {
    method: METHOD.POST,
    url: '/forgot-password',
  },
  CHANGE_PASSWORD: {
    method: METHOD.POST,
    url: '/change-password',
  },
  CONTACT: {
    method: METHOD.POST,
    url: '/contact',
  },
  CHART_FULL: {
    method: METHOD.GET,
    url: '/chart/prediction/:ticker',
  },
  CHART_SMALL: {
    method: METHOD.GET,
    url: '/chart/small/:ticker',
  },
  CHART_FUTURE: {
    method: METHOD.POST,
    url: '/chart/future/:ticker',
  },
  CHART_VECTOR: {
    method: METHOD.POST,
    url: '/chart/vector/:ticker',
  },
  TICKERS_LOAD: {
    method: METHOD.GET,
    url: '/tickers',
  },
  SEARCH: {
    method: METHOD.POST,
    url: '/search',
  },
  VERIFICATION: {
    method: METHOD.POST,
    url: '/verification',
  },
  RESEND_VERIFICATION: {
    method: METHOD.POST,
    url: '/resend-verification',
  },
  PLAN: {
    method: METHOD.GET,
    url: '/plan/:planId',
  },
  PROMO: {
    method: METHOD.POST,
    url: '/promo/:planId',
  },
  AUTHORIZE: {
    method: METHOD.POST,
    url: '/create/authorize',
  },
  PAYPAL: {
    method: METHOD.POST,
    url: '/create/paypal',
  },
  STRIPE_CHECKOUT: {
    method: METHOD.POST,
    url: '/stripe/checkout-session',
  },
  UNSUBSCRIBE: {
    method: METHOD.POST,
    url: '/unsubscribe',
  },
  ORDERS: {
    method: METHOD.GET,
    url: '/orders',
  },
  CANCEL_SUBSCRIPTION: {
    method: METHOD.POST,
    url: '/cancel',
  },
  REPORTS: {
    method: METHOD.POST,
    url: '/reports/',
  },
  PREF_SAVE: {
    method: METHOD.POST,
    url: '/preferences/:name',
  },
  STRATEGIES_BACKTEST: {
    method: METHOD.POST,
    url: '/strategies/backtest',
  },
  STRATEGIES_LIST: {
    method: METHOD.GET,
    url: '/strategies',
  },
  STRATEGIES_FIELDS: {
    method: METHOD.GET,
    url: '/strategies/fields',
  },
  STRATEGIES_SAVE: {
    method: METHOD.POST,
    url: '/strategies/save',
  },
  STRATEGIES_RECOMMENDATION: {
    method: METHOD.GET,
    url: '/strategies/recommendation',
  },
  STRATEGIES_MARKETPLACE_PUBLISH: {
    method: METHOD.POST,
    url: '/strategies/marketplace/publish',
  },
  STRATEGIES_MARKETPLACE_UNPUBLISH: {
    method: METHOD.POST,
    url: '/strategies/marketplace/unpublish',
  },
  STRATEGIES_MARKETPLACE_MINE: {
    method: METHOD.GET,
    url: '/strategies/marketplace/mine',
  },
  STRATEGIES_MARKETPLACE_WINDOWS: {
    method: METHOD.GET,
    url: '/strategies/marketplace/windows',
  },
  STRATEGIES_MARKETPLACE_LEADERBOARD: {
    method: METHOD.GET,
    url: '/strategies/marketplace/leaderboard',
  },
  STRATEGIES_MARKETPLACE_STRATEGY: {
    method: METHOD.GET,
    url: '/strategies/marketplace/strategy',
  },
  STRATEGIES_MARKETPLACE_CLONE: {
    method: METHOD.POST,
    url: '/strategies/marketplace/clone',
  },
  STRATEGIES_OPTIONS_CONTRACTS: {
    method: METHOD.GET,
    url: '/strategies/options/contracts',
  },
  STRATEGIES_OPTIONS_SYNC: {
    method: METHOD.POST,
    url: '/strategies/options/sync',
  },
  STRATEGIES_OPTIONS_BACKTEST: {
    method: METHOD.POST,
    url: '/strategies/options/backtest',
  },
  STRATEGIES_OPTIONS_SELECT_CONTRACT: {
    method: METHOD.POST,
    url: '/strategies/options/select-contract',
  },
  STRATEGIES_OPTIONS_RISK_ACKNOWLEDGEMENT: {
    method: METHOD.GET,
    url: '/strategies/options/risk-acknowledgement',
  },
  STRATEGIES_OPTIONS_ACCEPT_RISK_ACKNOWLEDGEMENT: {
    method: METHOD.POST,
    url: '/strategies/options/risk-acknowledgement',
  },
  MARKET_ANALYSIS_LIST: {
    method: METHOD.GET,
    url: '/market-analysis/list',
  },
  MARKET_ANALYSIS_GENERAL: {
    method: METHOD.GET,
    url: '/market-analysis/general',
  },
  MARKET_ANALYSIS_GET: {
    method: METHOD.GET,
    url: '/market-analysis/:ticker',
  },
  BROKER_LINK_TOKEN: {
    method: METHOD.POST,
    url: '/plaid/deeptrading/link-token',
  },
  BROKER_EXCHANGE: {
    method: METHOD.POST,
    url: '/plaid/deeptrading/exchange-public-token',
  },
  BROKER_CONNECTIONS: {
    method: METHOD.GET,
    url: '/plaid/deeptrading/connections',
  },
  BROKER_ACCOUNTS_UPDATE: {
    method: METHOD.POST,
    url: '/plaid/deeptrading/accounts',
  },
  BROKER_DISCONNECT: {
    method: METHOD.POST,
    url: '/plaid/deeptrading/disconnect',
  },
  BROKER_REFRESH: {
    method: METHOD.POST,
    url: '/plaid/deeptrading/refresh',
  },
  BROKER_HOLDINGS: {
    method: METHOD.GET,
    url: '/plaid/deeptrading/holdings',
  },
  BROKER_REQUEST_SUPPORT: {
    method: METHOD.POST,
    url: '/plaid/deeptrading/request-support',
  },
}

const none = {};

const DEFAULT_TIMEOUT_MS = Number(process.env.REACT_APP_API_TIMEOUT_MS) || 120000;

const useApi = (controller, options = none) => {
  const [loading, setLoading] = React.useState(null);
  const [payload, setPayload] = React.useState();
  const {setResponse} = React.useContext(StoreContext);

  const execute = React.useCallback(async (data = undefined, {urlPath, urlParams} = {}) => {
    setLoading(true);
    try {
      const resolvedPath = generatePath(
        controller.url,
        Object.assign({}, options?.urlParams || {}, urlParams || {}),
      );
      const requestUrl = `${resolvedPath}${urlPath || options?.urlPath || ''}`;

      const response = await axios.request({
        ...(controller || {}),
        url: requestUrl,
        data,
        baseURL: '/api/',
        headers: {'X-Request-Id': v4()},
        timeout: DEFAULT_TIMEOUT_MS,
      });
      setPayload(response.data);
      setResponse(response.data);
      return response.data;
    } catch (err) {
      const responseData = err?.response?.data || {
        success: false,
        message: err?.message || 'Request failed',
      };
      setPayload(responseData);
      setResponse(responseData);
      return responseData;
    } finally {
      setLoading(false);
    }
  }, [controller, options?.urlPath, options?.urlParams, setResponse]);

  return React.useMemo(() => ({loading, execute, payload}), [execute, loading, payload]);
};

export default useApi;
